<template>
  <b-modal
    ref="modal-aufgabe-edit"
    auto-focus-button="ok"
    id="modalAufgabeEdit"
    content-class="modal-form"
    title="Aufgabe erstellen"
    centered
    @hidden="resetFields"
    @show="onShow"
    no-close-on-backdrop
    data-bs-focus="false"
  >
    <b-overlay :show="isBusy">
      <b-form ref="form" class="aufgabe-form" @submit.stop.prevent="handleSubmit">
        <div class="d-flex align-items-end" style="gap: 8px">
          <b-form-group style="flex: 1 1 0; min-width: 0" label-for="bearbeiter">
            <template #label>
              <User :size="16" class="mr-1" style="color: #3699ff" />
              Bearbeiter/in
            </template>
            <AppSelect
              v-model="$v.form.bearbeiter.$model"
              :options="zugewiesenePersonOptions"
              :disabled="form.team !== null"
              :state="form.bearbeiter !== null ? true : null"
              label="Bearbeiter/in"
              placeholder="Wähle Bearbeiter/in"
              no-results-text="Bearbeiter/in nicht gefunden"
            />
          </b-form-group>
          <b-button
            @click="form.bearbeiter = currentUserId"
            style="height: 38px; flex-shrink: 0"
            class="dialog-btn-secondary"
            size="sm"
            :disabled="form.bearbeiter === currentUserId || form.team !== null"
          >
            Ich
          </b-button>
          <b-form-group style="flex: 1 1 0; min-width: 0" label-for="team">
            <template #label>
              <Users :size="16" class="mr-1" style="color: #3699ff" />
              Team
            </template>
            <AppSelect
              v-model="$v.form.team.$model"
              :options="teamOptions"
              :disabled="form.bearbeiter !== null"
              :state="form.team !== null ? true : null"
              label="Team"
              placeholder="oder ein Team"
              hide-search
            />
          </b-form-group>
          <div style="width: 14px; height: 38px; display: flex; align-items: center; flex-shrink: 0">
            <i
              v-if="form.bearbeiter !== null || form.team !== null"
              class="fas fa-times text-danger"
              @click="resetPersonOderTeam"
              style="cursor: pointer"
            ></i>
          </div>
        </div>
        <!-- Kategorie -->
        <div class="d-flex align-items-end">
          <b-form-group class="flex-fill" label-for="kategorie">
            <template #label>
              <Tag :size="16" class="mr-1" style="color: #3699ff" />
              Kategorie
            </template>
            <AppSelect
              v-model="$v.form.kategorie.$model"
              :options="kategorieOptions"
              :state="form.kategorie !== null ? true : null"
              label="Kategorie"
              placeholder="Wähle eine Kategorie"
              no-results-text="Keine Kategorie gefunden."
            />
          </b-form-group>

          <div
            class="ml-2"
            style="width: 14px; height: 38px; display: flex; align-items: center; flex-shrink: 0"
          >
            <i
              v-if="form.kategorie !== null"
              class="fas fa-times text-danger"
              @click="resetKategorie"
              style="cursor: pointer"
            ></i>
          </div>
        </div>
        <!-- Priorität + Fälligkeitsdatum -->
        <div class="d-flex" style="gap: 16px">
          <div class="d-flex align-items-end" style="gap: 8px; flex: 1; min-width: 0">
            <b-form-group label-for="prioritaet" class="flex-fill">
              <template #label>
                <CircleAlert :size="16" class="mr-1" style="color: #3699ff" />
                Priorität
              </template>
              <AppSelect
                v-model="$v.form.prioritaet.$model"
                :options="prioritaetOptions"
                :state="form.prioritaet !== null ? true : null"
                label="Priorität"
                placeholder="Wähle eine Priorität"
                hide-search
              />
            </b-form-group>
            <div style="width: 14px; height: 38px; display: flex; align-items: center; flex-shrink: 0">
              <i
                v-if="form.prioritaet !== null"
                class="fas fa-times text-danger"
                @click="resetPrioritaet"
                style="cursor: pointer"
              ></i>
            </div>
          </div>
          <div class="d-flex align-items-end" style="gap: 8px; flex: 1; min-width: 0">
            <b-form-group
              class="flex-fill"
              label-for="datepicker"
              :invalid-feedback="
                $v.form.faelligkeitsdatum.$error && form.faelligkeitsdatum !== ''
                  ? 'Please select a date in the future.'
                  : ''
              "
            >
              <template #label>
                <Calendar :size="16" class="mr-1" style="color: #3699ff" />
                Fälligkeitsdatum
              </template>
              <AppDatePicker
                id="date-picker"
                v-model="$v.form.faelligkeitsdatum.$model"
                value-type="YYYY-MM-DD"
                :min="minFaelligkeitsdatum"
                :state="!$v.form.faelligkeitsdatum.$error ? true : null"
                placeholder="Wähle ein Datum"
                :disabled-date="dateDisabled"
              />
            </b-form-group>
            <div style="width: 14px; height: 38px; display: flex; align-items: center; flex-shrink: 0">
              <i
                v-if="form.faelligkeitsdatum !== null"
                class="fas fa-times text-danger"
                @click="resetFaelligkeitsdatum"
                style="cursor: pointer"
              ></i>
            </div>
          </div>
        </div>
        <!-- Beschreibung -->
        <b-form-group label="Beschreibung" label-for="beschreibung" style="margin-right: 22px">
          <template #label>
            <div class="d-flex align-items-center justify-content-between w-100">
              <span>
                <PencilLine :size="16" class="mr-1" style="color: #3699ff" />
                Beschreibung
              </span>
              <Maximize2
                v-if="!isBeschreibungExpanded"
                :size="16"
                style="cursor: pointer; color: #3699ff"
                @click="toggleBeschreibungsFeldSize"
              />
            </div>
          </template>
          <div :class="[{ 'beschreibung-expanded': isBeschreibungExpanded }]">
            <div v-if="isBeschreibungExpanded" class="d-flex justify-content-between align-items-center mb-2">
              <span class="h4">Beschreibung</span>
              <Minimize2
                :size="16"
                style="cursor: pointer; color: #3699ff"
                @click="toggleBeschreibungsFeldSize"
              />
            </div>

            <div :class="{ 'beschreibung-filled': form.beschreibung }">
              <Editor
                v-model="$v.form.beschreibung.$model"
                placeholder="Schreibe eine Beschreibung"
                :disableMentions="true"
                @imagePaste="onImagePaste"
              />
            </div>
          </div>
        </b-form-group>
        <!-- Datei-Upload -->
        <b-form-group label-for="dateiUpload" style="margin-right: 22px">
          <template #label>
            <FileUp :size="16" class="mr-1" style="color: #3699ff" />
            Datei-Upload
          </template>
          <DateiUpload v-model="dateien" />
        </b-form-group>
        <!-- Verlinkungen -->
        <b-form-group label-for="verlinkungen">
          <template #label>
            <Link :size="16" class="mr-1" style="color: #3699ff" />
            Verlinkungen
          </template>
          <VerlinkungenEditor
            ref="verlinkungenEditor"
            v-model="verlinkungen"
            :allowed-types="verlinkungenTypen"
            :disabled="fromFVC || fromRepru"
            :hide-add-button="disableAttachments"
            @valid="verlinkungenValid = $event"
            @reisetermin-selected="onReiseterminSelected"
            @vorgang-valid="onVorgangValid"
          />
        </b-form-group>
        <div
          v-if="enableManualTask && reiseterminVerlinkungenCount > 1"
          class="d-flex align-items-center justify-content-between mt-2"
          style="
            background-color: #eff6ff;
            border: 0.8px solid #dbeafe;
            border-radius: 10px;
            padding: 0 16px;
            height: 53px;
            margin-right: 22px;
          "
        >
          <span style="font-size: 14px; color: #101828">Pro Termin eine Aufgabe</span>
          <b-form-checkbox
            v-model="proTerminEineAufgabeChecked"
            switch
            size="lg"
            class="mb-0 ml-2"
          ></b-form-checkbox>
        </div>
      </b-form>
    </b-overlay>

    <template #modal-footer="{ hide }" class="pl-1">
      <b-button @click="hide" :disabled="isBusy" class="dialog-btn-secondary">Abbrechen</b-button>
      <b-button
        :disabled="disableSichernButton"
        variant="primary"
        @click="handleSubmit(hide)"
        class="dialog-btn"
      >
        Erstellen
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  checkDateInFuture,
  formatSegmentStrecke,
  changeTeamsToCorrectShape,
} from '@/core/common/helpers/utils.js';
import { mapState, mapGetters } from 'vuex';
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import { CREATE_AUFGABE } from '@/core/aufgaben/stores/aufgaben.module';
import Editor from '@/components/common/editor.vue';
import AppSelect from '@/components/common/app-select/app-select.vue';
import AppDatePicker from '@/components/common/app-date-picker/app-date-picker.vue';
import apiService from '@/core/common/services/api.service';
import { uploadDatei, getReferenzierteBildDateinamen } from '@/core/common/helpers/datei.utils';
import VerlinkungenEditor from '@/components/aufgaben/verlinkungen-editor.vue';
import { textbausteine } from '@/core/common/helpers/textbausteine';
import {
  subBusinessDays,
  addDays,
  addBusinessDays,
  nextMonday,
  isWeekend,
  format,
  subMonths,
  isBefore,
} from 'date-fns';
import * as odataService from '@/core/common/services/odata.service';
import {
  User,
  Users,
  Tag,
  CircleAlert,
  Calendar,
  Link,
  PencilLine,
  Maximize2,
  Minimize2,
  FileUp,
} from 'lucide-vue';
import DateiUpload from '@/components/common/datei-upload.vue';
import { AUFGABENGRUPPE_REPRU } from '@/core/common/helpers/aufgabengruppen.js';

// Kategorien, deren Fälligkeit sich aus dem Abflugdatum des Reisetermins ergibt
const FAELLIGKEIT_MONATE_VOR_ABFLUG = {
  VerbindlicheGruppenIndividualisierung: 6,
  FlugSonderservices: 1,
  UnkritischerNameChange: 1,
};

export default {
  mixins: [validationMixin],
  name: 'AufgabeErstellenModal',
  components: {
    AppDatePicker,
    VerlinkungenEditor,
    Editor,
    AppSelect,
    User,
    Users,
    Tag,
    CircleAlert,
    Calendar,
    Link,
    PencilLine,
    Maximize2,
    Minimize2,
    FileUp,
    DateiUpload,
  },
  props: {
    prefixedVerlinkungen: {
      type: Array,
      default: () => {
        return [];
      },
    },
    disableAttachments: { type: Boolean, default: false },
    fromFVC: { type: Boolean, default: false },
    enableManualTask: { type: Boolean, default: false },
    fromRepru: { type: Boolean, default: false },
  },
  watch: {
    prefixedVerlinkungen(v) {
      this.verlinkungen = v;
    },
    // Entfernt ein eingefügtes Bild auch aus der lokalen Datei-Liste, sobald es aus dem Text gelöscht wird
    'form.beschreibung'() {
      const referenzierteBilder = getReferenzierteBildDateinamen(this.form.beschreibung);
      referenzierteBilder.forEach(name => this.bildDateinamen.add(name));
      // Eingefügte Bilder entfernen, die mal im Text standen
      this.dateien = this.dateien.filter(
        file =>
          !/^Bild_/.test(file.name) ||
          referenzierteBilder.has(file.name) ||
          !this.bildDateinamen.has(file.name)
      );
    },
    'form.kategorie'(newVal) {
      const kategorie = newVal;
      if (kategorie === 'OptionsfristErreicht') {
        this.form.beschreibung = `<div> ${textbausteine.headerTextBeschreibungBeiGprOptionsfrist} </div>
            ${textbausteine.grpOptionsfristTodos}`;
      }
      // NUR beschreibung
      else if (
        kategorie === 'ZimmerkontingentErhoehen' ||
        kategorie === 'ZusatzterminePruefen' ||
        kategorie === 'HotelwechselPruefen' ||
        kategorie === 'TerminAbsagen' ||
        kategorie === 'Terminplanung' ||
        kategorie === 'PaxForecasting' ||
        kategorie === 'ThPlanung' ||
        kategorie === 'Flugverfuegbarkeit' ||
        kategorie === 'GruppeTicketing' ||
        kategorie === 'DurchfuehrungUnterMtz'
      ) {
        this.form.beschreibung = textbausteine[kategorie] ?? '';
      }
      //////// wenn ein textbaustein nicht vorhanden ist, wird in der beschreibung auch nix gesetzt ////////
      // Prio HOCH und Beschreibung (falls vorhanden)
      else if (
        kategorie === 'ThVkReduktion' ||
        kategorie === 'VkReduktion' ||
        // haben keine beschreibung
        kategorie === 'PruefungThVkReduktion' ||
        kategorie === 'PruefungVkReduktion' ||
        kategorie === 'Flugpreisuebernahme' ||
        kategorie === 'Gruppenkostenuebernahme'
      ) {
        this.form.prioritaet = 2;
        this.form.beschreibung = textbausteine[kategorie] ?? '';
      }
      // Prio MITTEL, Fälligkeit morgen oder Montag und Beschreibung (falls vorhanden)
      else if (kategorie === 'PreiseUnterMtzAktualisieren' || kategorie === 'KundenschreibenVersenden') {
        this.form.prioritaet = 1;
        let naechsterWerktag = addDays(new Date(), 1); // Morgen
        if (isWeekend(naechsterWerktag)) {
          naechsterWerktag = nextMonday(naechsterWerktag);
        }
        this.form.faelligkeitsdatum = format(naechsterWerktag, 'yyyy-MM-dd');
        this.form.beschreibung = textbausteine[kategorie] ?? '';
      }
      // Prio Hoch, Fälligkeit morgen oder Montag und Beschreibung (falls vorhanden)
      else if (kategorie === 'UnverbindlichesFlugangebot' || kategorie === 'KlaerungVonLeistungen') {
        this.form.prioritaet = 2;
        let naechsterWerktag = addDays(new Date(), 1); // Morgen
        if (isWeekend(naechsterWerktag)) {
          naechsterWerktag = nextMonday(naechsterWerktag);
        }
        this.form.faelligkeitsdatum = format(naechsterWerktag, 'yyyy-MM-dd');
        this.form.beschreibung = textbausteine[kategorie] ?? '';
      }
      // Prio MITTEL, Fälligkeit in 5 Tagen und Beschreibung (falls vorhanden)
      else if (
        kategorie === 'FlugplanaenderungAbsprache' ||
        kategorie === 'InlandsflugAbsprache' ||
        kategorie === 'FlugKontaktdaten' ||
        kategorie === 'UnverbindlicheAnfrageFlugumbuchung' ||
        // hat keine beschreibung
        kategorie === 'GruppeSonstiges'
      ) {
        this.form.prioritaet = 1;
        const fuenfWerktage = addBusinessDays(new Date(), 5);
        this.form.faelligkeitsdatum = format(fuenfWerktage, 'yyyy-MM-dd');
        this.form.beschreibung = textbausteine[kategorie] ?? '';

        if (kategorie === 'FlugplanaenderungAbsprache') {
          // Prüfe, ob bereits ein Vorgang verlinkt ist und lade die Strecken
          const vorgangVerlinkung = this.verlinkungen.find(v => v.typ === 'Vorgang' && v.artifactId);
          if (vorgangVerlinkung && vorgangVerlinkung.artifactId.toString().length === 7) {
            this.loadStreckenForFlugplanaenderungAbsprache(vorgangVerlinkung.artifactId);
          }
        }
      }
      // Prio HOCH, Fälligkeit Heute oder Montag und Beschreibung (falls vorhanden)
      else if (
        kategorie === 'TicketingAnfrage' ||
        kategorie === 'StornoStaffelEinkaufspreiseAnpassen' ||
        kategorie === 'TransferkostenT4Pruefen' ||
        kategorie === 'EinkaufspreiseDerVerlaengerungPruefen' ||
        kategorie === 'TerminbezogeneAufpreisePruefen' ||
        kategorie === 'VerbindlicheFlugstornosUmbuchung' ||
        kategorie === 'KritischerNameChange' ||
        // haben keine beschreibung
        kategorie === 'VorgangsbezogeneAufpreisePruefen' ||
        kategorie === 'SofortigesTicketing'
      ) {
        this.form.prioritaet = 2;
        this.setFaelligkeitHeuteOderNachDemWochenende();
        this.form.beschreibung = textbausteine[kategorie] ?? '';
      }
      // Prio HOCH, Fälligkeit 2 Werktage nach Erstellung und Beschreibung (falls vorhanden)
      else if (kategorie === 'EinkaufspreisAbweichungKalkBlank' || kategorie === 'FlugSeats') {
        this.form.prioritaet = 2;
        this.form.faelligkeitsdatum = format(addBusinessDays(new Date(), 2), 'yyyy-MM-dd');
        this.form.beschreibung = textbausteine[kategorie] ?? '';
      }
      // Prio HOCH, Fälligkeit 3 Werktage nach Erstellung und Beschreibung
      else if (kategorie === 'Terminfreigabe') {
        this.form.prioritaet = 2;
        this.form.faelligkeitsdatum = format(addBusinessDays(new Date(), 3), 'yyyy-MM-dd');
        this.form.beschreibung = textbausteine[kategorie] ?? '';
      }
      // Prio HOCH, Fälligkeit 4 Tag und Beschreibung mit Kreditor und Gesamtbetrag
      else if (kategorie === 'TvzBuchen') {
        this.form.prioritaet = 2;
        const viertage = addDays(new Date(), 4);
        this.form.faelligkeitsdatum = format(viertage, 'yyyy-MM-dd');
        this.form.beschreibung = this.setBeschreibungMitKreditorUndGesamtbetrag(textbausteine.TvzBuchen);
      }
      // Fälligkeit 4 Tage und Beschreibung (falls vorhanden)
      else if (
        kategorie === 'KalkBlankRgAbweichung' ||
        kategorie === 'KalkBlankAbweichungNachCmRueckmeldung'
      ) {
        const vierWerktage = addBusinessDays(new Date(), 4);
        this.form.faelligkeitsdatum = format(vierWerktage, 'yyyy-MM-dd');
        this.form.beschreibung = textbausteine[kategorie] ?? '';
      }
      // Prio MITTEL und Beschreibung (falls vorhanden)
      else if (kategorie === 'ForecastAnpassungPruefen') {
        this.form.prioritaet = 1;
        this.form.beschreibung = textbausteine[kategorie] ?? '';
      }
      // Fälligkeit 5 Tage
      else if (kategorie === 'Gruppenkontingentanfrage' || kategorie === 'Gruppensammelquotierung') {
        const fuenfWerktage = addBusinessDays(new Date(), 5);
        this.form.faelligkeitsdatum = format(fuenfWerktage, 'yyyy-MM-dd');
      }
      // Fälligkeit 3 Tage
      else if (kategorie === 'FlugstammAnpassung') {
        const dreiWerktage = addBusinessDays(new Date(), 3);
        this.form.faelligkeitsdatum = format(dreiWerktage, 'yyyy-MM-dd');
      }
      // Prio Mittel, Fälligkeit 3 Tage und Beschreibung
      else if (kategorie === 'InlandsflugUeberTh') {
        this.form.prioritaet = 1;
        const dreiWerktage = addBusinessDays(new Date(), 3);
        this.form.faelligkeitsdatum = format(dreiWerktage, 'yyyy-MM-dd');
        this.form.beschreibung = textbausteine[kategorie] ?? '';
      }
      // Prio MITTEL, Fälligkeit 2 Werktage nach Erstellung
      else if (kategorie === 'RechnungPruefenUndBuchen' || kategorie === 'RechnungZahlen') {
        this.form.prioritaet = 1;
        this.form.faelligkeitsdatum = format(addBusinessDays(new Date(), 2), 'yyyy-MM-dd');
      }
      // Prio Mittel, Fälligkeit 6 Monate vor abflug (wenn das in der Vergangenheit ist “leer”) und beschreibung
      else if (kategorie === 'VerbindlicheGruppenIndividualisierung') {
        this.form.prioritaet = 1;
        const reiseterminVerlinkung = this.verlinkungen.find(v => v.typ === 'Reisetermin' && v.abreisedatum);
        if (reiseterminVerlinkung) {
          this.setFaelligkeitMonateVorAbflug(
            reiseterminVerlinkung.abreisedatum,
            FAELLIGKEIT_MONATE_VOR_ABFLUG[kategorie]
          );
        }
        this.form.beschreibung = textbausteine[kategorie] ?? '';
      }
      // Prio Niedrig, Fälligkeit 1 Monate vor abflug (wenn das in der Vergangenheit ist “leer”) und beschreibung
      else if (kategorie === 'FlugSonderservices' || kategorie === 'UnkritischerNameChange') {
        this.form.prioritaet = 0;
        const reiseterminVerlinkung = this.verlinkungen.find(v => v.typ === 'Reisetermin' && v.abreisedatum);
        if (reiseterminVerlinkung) {
          this.setFaelligkeitMonateVorAbflug(
            reiseterminVerlinkung.abreisedatum,
            FAELLIGKEIT_MONATE_VOR_ABFLUG[kategorie]
          );
        }
        this.form.beschreibung = textbausteine[kategorie] ?? '';
      }
      // Beschreibung leeren
      else if (!this.form.beschreibung) {
        this.form.beschreibung = '';
      }
    },
  },
  data() {
    return {
      mostContactedPeople: [],
      form: {
        faelligkeitsdatum: null,
        prioritaet: null,
        bearbeiter: null,
        beschreibung: null,
        kategorie: null,
        team: null,
      },
      dateien: [],
      // Dateinamen der bereits im Beschreibungstext aufgetauchten Bilder; dient dazu, einen
      // wieder aus dem Text entfernten Bild-Anhang aus der lokalen Datei-Liste zu entfernen
      bildDateinamen: new Set(),
      verlinkungen: [],
      verlinkungenValid: true,
      prioritaetOptions: [
        { value: 0, text: 'Niedrig' },
        { value: 1, text: 'Mittel' },
        { value: 2, text: 'Hoch' },
      ],
      proTerminEineAufgabeChecked: false,
      isBeschreibungExpanded: false,
    };
  },
  mounted() {
    this.$v.form.$touch(); // Trigger validation on component mount
    if (this.prefixedVerlinkungen?.length > 0) {
      this.verlinkungen = this.prefixedVerlinkungen;
    }
  },
  computed: {
    ...mapState({
      isBusy: state => state.aufgaben.isBusy,
      users: state => state.users['users'],
      kategorienConfig: state => state.stateConfig.kategorienConfig,
    }),
    ...mapGetters(['currentUserId', 'parsedRoles', 'getKategorienManuallyCreatable']),
    disableSichernButton() {
      return this.$v.form.$anyError || this.isBusy || !this.verlinkungenValid;
    },
    verlinkungenTypen() {
      if (this.fromFVC) return ['Flugverfügbarkeit'];
      if (this.fromRepru) return ['Reisetermin', 'Vorgang', 'Reise', 'Leistung'];
      return ['Reisetermin', 'Vorgang', 'Reise'];
    },
    reiseterminVerlinkungenCount() {
      return this.verlinkungen.filter(verlinkung => verlinkung.typ === 'Reisetermin').length;
    },
    zugewiesenePersonOptions() {
      return this.changePeopleToCorrectShape(this.users);
    },
    teamOptions() {
      const abkuerzungen = {
        'Destination Operations Management': 'DOM',
        'Destination Success Management': 'DSM',
        'Flight Operations Management': 'FOM',
      };
      return this.changeTeamsToCorrectShape(this.users).map(option => ({
        value: option.value,
        text: abkuerzungen[option.value] || option.text,
      }));
    },
    // Ermittelt das Department des aktuell eingeloggten Users aus der Mitarbeiterliste
    currentUserDepartment() {
      if (!this.users?.length || !this.currentUserId) return null;
      return this.users.find(user => user.id === this.currentUserId)?.department ?? null;
    },
    kategorieOptions() {
      if (!this.getKategorienManuallyCreatable) return [];

      let options = [...this.getKategorienManuallyCreatable];

      if (this.fromRepru && this.kategorienConfig) {
        options = options.filter(option => {
          if (option.value === 'Sonstiges') return true;
          const config = this.kategorienConfig[option.value];
          return config && config.aufgabengruppen?.includes(AUFGABENGRUPPE_REPRU);
        });
      }
      // Admins sind ausgenommen und sehen immer alle Kategorien
      if (this.kategorienConfig && !this.parsedRoles.includes('Admin')) {
        const department = this.currentUserDepartment;
        options = options.filter(option => {
          const teams = this.kategorienConfig[option.value]?.teamsAllowedToAddManually;
          return !teams || teams.length === 0 || (!!department && teams.includes(department));
        });
      }

      return options.sort((a, b) => {
        return a.text.localeCompare(b.text);
      });
    },
    minFaelligkeitsdatum() {
      return new Date();
    },
  },
  validations: {
    form: {
      faelligkeitsdatum: {
        required,
        checkDateInFuture,
      },
      prioritaet: {
        required,
      },
      beschreibung: {},
      bearbeiter: {
        required(v) {
          return this.form.team !== null || required(v);
        },
      },
      team: {
        required(v) {
          return this.form.bearbeiter !== null || required(v);
        },
      },
      kategorie: { required },
    },
  },
  methods: {
    setFaelligkeitHeuteOderNachDemWochenende() {
      let heute = new Date();
      if (isWeekend(heute)) {
        heute = nextMonday(heute);
      }
      this.form.faelligkeitsdatum = format(heute, 'yyyy-MM-dd');
    },
    changeTeamsToCorrectShape,
    changePeopleToCorrectShape(data) {
      const bearbeiterOptions = data
        .filter(({ givenName }) => givenName !== null && !givenName.toLowerCase().includes('test'))
        .filter(({ surname }) => surname !== null && !surname.toLowerCase().includes('test'))
        .filter(({ mail }) => mail !== null)
        .map(({ givenName, surname, id }) => ({
          value: id,
          text: givenName + ' ' + surname,
        }));
      if (this.mostContactedPeople.length > 0) {
        const order = this.mostContactedPeople.reduce((acc, curr, idx) => {
          return { ...acc, [curr.bearbeiter]: idx + 1 };
        }, {});
        return bearbeiterOptions.sort((a, b) => {
          const orderA = order[a.value] || this.mostContactedPeople.length + 1;
          const orderB = order[b.value] || this.mostContactedPeople.length + 1;

          if (orderA !== orderB) {
            return orderA - orderB;
          } else {
            return a.text.localeCompare(b.text);
          }
        });
      } else return bearbeiterOptions;
    },
    onReiseterminSelected({ unterlagenversandDatum, abreisedatum }) {
      const kategorie = this.form.kategorie;
      if (kategorie === 'HotelwechselPruefen') {
        this.changePriorityBasedOnReisetermin(unterlagenversandDatum);
      } else if (FAELLIGKEIT_MONATE_VOR_ABFLUG[kategorie] !== undefined) {
        this.setFaelligkeitMonateVorAbflug(abreisedatum, FAELLIGKEIT_MONATE_VOR_ABFLUG[kategorie]);
      }
    },
    setFaelligkeitMonateVorAbflug(abreisedatum, monate) {
      if (!abreisedatum) return;
      const faelligkeit = subMonths(new Date(abreisedatum), monate);
      if (isBefore(faelligkeit, new Date())) {
        this.form.faelligkeitsdatum = null;
      } else {
        this.form.faelligkeitsdatum = format(faelligkeit, 'yyyy-MM-dd');
      }
    },
    onVorgangValid(vorgangId) {
      this.addReiseterminAutomatisch(vorgangId);
      if (this.form.kategorie === 'FlugplanaenderungAbsprache') {
        this.loadStreckenForFlugplanaenderungAbsprache(vorgangId);
      }
    },
    changePriorityBasedOnReisetermin(unterlagenversandDatum) {
      let tageBisUnterlagenVersand = null;
      if (unterlagenversandDatum) {
        const versandDatum = new Date(unterlagenversandDatum);
        const heute = new Date();

        const tageUnterschied = versandDatum.getTime() - heute.getTime();
        tageBisUnterlagenVersand = Math.ceil(tageUnterschied / (1000 * 60 * 60 * 24));

        if (tageBisUnterlagenVersand <= 10) {
          this.form.prioritaet = 2;
        } else if (tageBisUnterlagenVersand <= 21) {
          this.form.prioritaet = 1;
        } else {
          this.form.prioritaet = 0;
        }
      }

      const faelligkeitsdatum = subBusinessDays(new Date(unterlagenversandDatum), 4);

      // formattiert es so, dass es für das form.faelligkeitsdatum passt
      this.form.faelligkeitsdatum = faelligkeitsdatum.toISOString().split('T')[0];
    },
    addReiseterminAutomatisch(input) {
      if (input && input?.length === 7) {
        apiService
          .get(
            `/Reisetermin?$filter=((hotelleistungen/any(hotelleistungen:hotelleistungen/isDeleted eq false and hotelleistungen/vorgangId eq ${input})))&$select=reiseterminkuerzel, id`
          )
          .then(response => {
            if (response.status === 200) {
              // filtert über die liste der Reisetermine und behält nur die Einträge die noch nicht vorhanden sind
              const uniqueReisetermine = response.data.result.value.filter(
                item =>
                  // überprüft dann, ob es diesen Reisetermin mit der artifactId schon gibt
                  !this.verlinkungen.some(
                    verlinkung => verlinkung.artifactId === item.id && verlinkung.typ === 'Reisetermin'
                  )
              );
              if (uniqueReisetermine.length === 0) {
                this.$bvToast.toast('Der Reisetermin existiert bereits', {
                  title: 'Warnung',
                  variant: 'warning',
                  solid: true,
                });
              } else {
                const newVerlinkungen = response.data.result.value.map((item, i) => ({
                  id: this.verlinkungen.length + i,
                  typ: 'Reisetermin',
                  artifactId: item.id,
                  artifactText: item.reiseterminkuerzel,
                }));

                this.verlinkungen = [...this.verlinkungen, ...newVerlinkungen];
              }
            }
          });
      }
    },
    async loadStreckenForFlugplanaenderungAbsprache(vorgangId) {
      try {
        const response = await odataService.getVorgang({
          filter: { id: parseInt(vorgangId) },
          expand: {
            teilnehmers: {
              filter: { deletedAt: null },
              expand: {
                teilnehmerReiseterminFlugstrecken: {
                  expand: {
                    segmente: {},
                  },
                },
              },
            },
          },
        });

        if (response?.data && response.data.length > 0) {
          const vorgang = response.data[0];

          if (vorgang.teilnehmers && vorgang.teilnehmers.length > 0) {
            const alleSegmente = vorgang.teilnehmers.flatMap(teilnehmer =>
              (teilnehmer.teilnehmerReiseterminFlugstrecken || []).flatMap(
                flugstrecke => flugstrecke.segmente || []
              )
            );

            // Filtere Segmente mit Status "TK" oder "UN"
            const segmenteMitTKoderUN = alleSegmente
              .filter(segment => segment.segmentStatus === 'TK' || segment.segmentStatus === 'UN')
              .sort((a, b) => a.segmentNummer - b.segmentNummer);

            if (segmenteMitTKoderUN.length > 0) {
              const alleFormatiertenStrecken = segmenteMitTKoderUN.map(segment =>
                formatSegmentStrecke(segment)
              );

              const uniqueStrecken = [...new Set(alleFormatiertenStrecken)];
              const formatierteStrecken = uniqueStrecken.join('<br /><br />');

              this.updateBeschreibungMitStrecken(formatierteStrecken);
            }
          }
        }
      } catch (error) {
        console.error('Fehler beim Laden der betroffenen Strecken:', error);
      }
    },
    updateBeschreibungMitStrecken(formatierteStrecken) {
      // Ersetze den Platzhalter in der Beschreibung mit den formatierten Strecken
      const streckenPlatzhalter = '<b>Betroffene Strecke(n):</b>\n  <br />';
      const neuerStreckenText = `<b>Betroffene Strecke(n):</b>\n  <br />${formatierteStrecken}`;

      if (this.form.beschreibung && this.form.beschreibung.includes(streckenPlatzhalter)) {
        this.form.beschreibung = this.form.beschreibung.replace(streckenPlatzhalter, neuerStreckenText);
      }
    },
    resetFields() {
      this.form = {
        faelligkeitsdatum: null,
        prioritaet: null,
        bearbeiter: null,
        beschreibung: null,
        kategorie: null,
        team: null,
      };
      this.dateien = [];
      // Beim Zurücksetzen des Formulars auch die gemerkten Bildkennungen leeren
      this.bildDateinamen = new Set();
      this.verlinkungen = [];
      this.proTerminEineAufgabeChecked = false;
      this.isBeschreibungExpanded = false;
    },
    resetPersonOderTeam() {
      this.form = { ...this.form, bearbeiter: null, team: null };
    },
    resetPrioritaet() {
      this.form = { ...this.form, prioritaet: null };
    },
    resetFaelligkeitsdatum() {
      this.form = { ...this.form, faelligkeitsdatum: null };
    },
    resetKategorie() {
      this.form = { ...this.form, kategorie: null };
      this.form.beschreibung = null;
      this.form.faelligkeitsdatum = null;
      this.form.prioritaet = null;
    },
    async handleSubmit(hide) {
      this.$v.form.$touch();
      if (this.$v.form.$anyError) {
        return;
      }
      let params = { ...this.$v.form.$model };

      const vorgangVerlinkungen = this.verlinkungen.filter(verlinkungen => verlinkungen.typ === 'Vorgang');
      const reiseterminVerlinkungen = this.verlinkungen.filter(
        verlinkungen => verlinkungen.typ === 'Reisetermin'
      );
      const reiseVerlinkungen = this.verlinkungen.filter(verlinkungen => verlinkungen.typ === 'Reise');
      const flugverfuegbarkeitVerlinkungen = this.verlinkungen.filter(
        verlinkungen => verlinkungen.typ === 'Flugverfügbarkeit'
      );
      const leistungVerlinkungen = this.verlinkungen.filter(verlinkungen => verlinkungen.typ === 'Leistung');

      if (this.verlinkungen.length > 0) {
        const verlinkungenFormatted = {
          ...(vorgangVerlinkungen.length > 0 && {
            vorgaenge: vorgangVerlinkungen.map(verlinkung => parseInt(verlinkung.artifactId)),
          }),
          ...(reiseterminVerlinkungen.length > 0 && {
            reiseTermine: reiseterminVerlinkungen.map(verlinkung => parseInt(verlinkung.artifactId)),
          }),
          ...(reiseVerlinkungen.length > 0 && {
            reisen: reiseVerlinkungen.map(verlinkung => parseInt(verlinkung.artifactId)),
          }),
          ...(flugverfuegbarkeitVerlinkungen.length > 0 && {
            flugverfuegbarkeiten: flugverfuegbarkeitVerlinkungen.map(verlinkung =>
              parseInt(verlinkung.artifactId)
            ),
          }),
          ...(leistungVerlinkungen.length > 0 && {
            leistungen: leistungVerlinkungen.map(verlinkung => parseInt(verlinkung.artifactId)),
          }),
        };
        params = {
          ...params,
          ...verlinkungenFormatted,
        };
      }

      if (this.proTerminEineAufgabeChecked) {
        // Für jeden Reisetermin eine eigene Aufgabe erstellen
        for (const verlinkung of reiseterminVerlinkungen) {
          const paramsFuerEinzelneAufgabe = {
            ...params,
            reiseTermine: [parseInt(verlinkung.artifactId)],
          };

          const response = await this.$store.dispatch(CREATE_AUFGABE, paramsFuerEinzelneAufgabe);
          await this.uploadDateien(response?.data?.result);
        }

        for (const flugverfuegbarkeitVerlinkung of flugverfuegbarkeitVerlinkungen) {
          const paramsFuerEinzelneAufgabe = {
            ...params,
            flugverfuegbarkeiten: [parseInt(flugverfuegbarkeitVerlinkung.artifactId)],
          };

          const response = await this.$store.dispatch(CREATE_AUFGABE, paramsFuerEinzelneAufgabe);
          await this.uploadDateien(response?.data?.result);
        }

        // damit gesagt wird, dass mehrere Aufgaben erstellt werden
        this.$emit('aufgabeErstellt', 'bulk');
      } else {
        // Eine Aufgabe erstellen und Erfolgsbenachrichtigung anzeigen
        const response = await this.$store.dispatch(CREATE_AUFGABE, params);
        console.log('response:', response);

        await this.uploadDateien(response?.data?.result);
        this.toast('Aufgabe wurde erstellt.');

        if (flugverfuegbarkeitVerlinkungen.length > 1) {
          this.$emit('aufgabeErstellt', 'bulk');
        } else if (flugverfuegbarkeitVerlinkungen.length === 1) {
          this.$emit('aufgabeErstellt', 'single');
        } else {
          this.$emit('aufgabeErstellt');
        }
      }
      hide();
    },
    async uploadDateien(aufgabeId) {
      if (!aufgabeId || this.dateien.length === 0) return;
      // Eingefügte Bilder, die vor dem Absenden wieder aus der Beschreibung entfernt wurden,
      // werden nicht als Anhang hochgeladen
      const referenzierteBilder = getReferenzierteBildDateinamen(this.form.beschreibung);
      const hochzuladen = this.dateien.filter(
        file => !/^Bild_/.test(file.name) || referenzierteBilder.has(file.name)
      );
      for (const file of hochzuladen) {
        await uploadDatei(aufgabeId, file);
      }
    },
    // Übernimmt ein aus der Zwischenablage eingefügtes Bild in die lokale Datei-Liste
    // Die Datei durchläuft anschließend die normale Validierung und Duplikats-Prüfung der DateiUpload-Komponente
    onImagePaste(file) {
      this.dateien = [...this.dateien, file];
    },
    onShow() {
      this.loadMostContactedEditors();
    },
    loadMostContactedEditors() {
      const mostContactedQuery = `/aufgaben/?apply=filter(autor eq '${this.currentUserId}')/groupby((bearbeiter),aggregate($count as count))`;
      apiService.get(mostContactedQuery).then(resp => {
        const result = resp.data.result.value;
        const sortedResult = result.sort((a, b) => b.count - a.count);
        this.mostContactedPeople = sortedResult;
      });
    },
    toggleBeschreibungsFeldSize() {
      this.isBeschreibungExpanded = !this.isBeschreibungExpanded;
    },
    dateDisabled(date) {
      const weekday = date.getDay();
      return weekday === 0 || weekday === 6;
    },
    setBeschreibungMitKreditorUndGesamtbetrag(beschreibung) {
      const leistungVerlinkungen = this.verlinkungen.filter(
        verlinkung => verlinkung.typ === 'Leistung' && verlinkung.betrag != null
      );

      if (leistungVerlinkungen.length > 0) {
        // Kreditor_Betrag_Währung Format erstellen
        const kreditoren = [
          ...new Set(leistungVerlinkungen.map(verlinkung => verlinkung.kreditor).filter(Boolean)),
        ];

        const waehrungen = [...new Set(leistungVerlinkungen.map(verlinkung => verlinkung.waehrung))];
        const hasMultipleWaehrungen = waehrungen.length > 1;

        const gesamtbetrag = leistungVerlinkungen.reduce(
          (sum, verlinkung) => sum + (verlinkung.betrag || 0),
          0
        );
        const waehrung = leistungVerlinkungen[0]?.waehrung || 'EUR';
        const formattedBetrag = hasMultipleWaehrungen
          ? '-'
          : gesamtbetrag.toLocaleString('de-DE', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });

        const kreditorBetragWaehrung =
          kreditoren.length > 0
            ? `${kreditoren.join(', ')}_${formattedBetrag}_${hasMultipleWaehrungen ? '-' : waehrung}`
            : '';

        beschreibung = beschreibung.replace('[Kreditor_Betrag_Währung]', kreditorBetragWaehrung);

        // Prüfe ob einer der Kreditoren eine Abschlagsrechnung hat
        const abschlagsrechnungKreditoren = ['ACQF', 'AVBRA', 'HAPMY', 'MALLA', 'SCENI', 'TENNZ'];
        const hatAbschlagsrechnung = kreditoren.some(kreditor =>
          abschlagsrechnungKreditoren.includes(kreditor)
        );

        if (hatAbschlagsrechnung) {
          beschreibung +=
            '<br /><span style="color: red;">Achtung: Für diesen Kreditor existiert eine Abschlagsrechnung!</span>';
        }

        return beschreibung;
      }

      return beschreibung;
    },
  },
};
</script>
<style scoped>
/* Formularzeilen im 16px-Raster; der Abstand kommt aus dem gap, nicht aus dem
   Theme-Margin der form-group (1.75rem = 22,75px) */
.aufgabe-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Ohne :deep, damit nur die form-groups dieses Templates getroffen werden und nicht
   die in Editor, DateiUpload oder VerlinkungenEditor */
.aufgabe-form .form-group {
  margin-bottom: 0;
}

/* Hide number input spinners */
:deep(input[type='number'])::-webkit-outer-spin-button,
:deep(input[type='number'])::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Modal width */
:deep(.modal-dialog) {
  max-width: 562px;
}

:deep(.b-calendar .btn:hover) {
  background-color: #d6dbe0 !important;
}

:deep(label[for='bearbeiter']::after),
:deep(label[for='prioritaet']::after),
:deep(label[for='kategorie']::after),
:deep(label[for='datepicker']::after),
:deep(label[for='team']::after) {
  content: ' *';
  color: var(--danger);
}

.beschreibung-expanded {
  position: fixed;
  top: 50px;
  left: 50px;
  right: 50px;
  bottom: 50px;
  z-index: 1050;
  background: white;
  padding: 20px;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.2);
  border-radius: 5px;
}

:deep(.tiptap) {
  background: #f3f3f5;
  border: 0.8px solid var(--border);
  border-radius: 0 0 8px 8px;
  outline: none;
}

.beschreibung-filled :deep(.tiptap) {
  border-color: var(--primary);
}

:deep(p.is-editor-empty:first-child::before) {
  color: #adadad !important;
}

:deep(.toolbar) {
  background-color: #f3f3f5;
  border: 0.8px solid var(--border);
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  margin-bottom: 2px;
}
</style>
